import { useQuery } from '@tanstack/react-query';
import { getEmployeeDetails, employeeKeys } from '../services/employeeApiService';
import type { EmployeeDetailsDto } from '../services/employeeApiService';

interface UseEmployeeDetailsResult {
  employee: EmployeeDetailsDto | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches comprehensive employee details (the single `/employees/{id}/details`
 * endpoint — a superset of the old `getEmployeeById` response, which hit the
 * exact same URL). Backed by React Query so ProfilePage no longer needs a
 * second, separate fetch for the same data.
 */
export function useEmployeeDetails(employeeId?: string, tenantId?: string): UseEmployeeDetailsResult {
  const { data, isLoading, error, refetch } = useQuery<EmployeeDetailsDto, Error>({
    queryKey: employeeKeys.detail(employeeId ?? '', tenantId ?? ''),
    queryFn: () => getEmployeeDetails(employeeId!, tenantId!),
    enabled: Boolean(employeeId && tenantId),
    staleTime: 60_000,
  });

  return {
    employee: data ?? null,
    loading: isLoading,
    error: error?.message ?? null,
    refetch: async () => {
      await refetch();
    },
  };
}
